import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, HelpCircle, Loader2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Field } from './fields';
import { wget } from './api';

export const CHECK_LABEL = { available: 'Looks available — we’ll confirm when we register it', taken: 'Already taken', unknown: 'Can’t check this ending — we’ll check for you', invalid: 'Enter a full domain, e.g. smithresin.co.uk' };
export const CHECK_TONE = { available: 'bg-emerald-100 text-emerald-800', taken: 'bg-rose-100 text-rose-800', unknown: 'bg-slate-100 text-slate-600', invalid: 'bg-slate-100 text-slate-600' };

export function DomainPill({ status, compact }) {
  if (!status) return null;
  const Icon = status === 'available' ? CheckCircle2 : status === 'taken' ? XCircle : HelpCircle;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${CHECK_TONE[status]}`} data-status={status}><Icon className="w-3 h-3" /> {compact ? { available: 'Looks available', taken: 'Taken', unknown: 'Unchecked', invalid: 'Invalid' }[status] : CHECK_LABEL[status]}</span>;
}

/** Domain text field with live (debounced) availability check + alternative suggestions. `onResult(domain, status)` lets the parent persist the result. */
export function DomainInput({ id, label, value, onChange, optional, placeholder, onResult }) {
  const [state, setState] = useState(null); // {status, suggestions, domain}
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    const v = (value || '').trim();
    if (!v || v.length < 4 || !v.includes('.')) { setState(null); return undefined; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { const d = await wget(`/website/domain-check?domain=${encodeURIComponent(v)}`); setState(d); onResult?.(d.domain, d.status); } catch { setState(null); } finally { setBusy(false); }
    }, 650);
    return () => clearTimeout(timer.current);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Field id={id} label={label} optional={optional}>
      <div className="relative">
        <Input id={id} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} inputMode="url" autoCapitalize="none" data-testid={`ww-${id}`} className="h-11 text-base pr-9" />
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>
      {state && !busy && <div className="mt-1.5" data-testid={`ww-${id}-check`}><DomainPill status={state.status} /></div>}
      {state?.status === 'taken' && !!state.suggestions?.length && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`ww-${id}-suggestions`}>
          <span className="text-[11px] text-slate-500 self-center">Try:</span>
          {state.suggestions.map((s) => <button key={s} type="button" onClick={() => onChange(s)} data-testid={`ww-${id}-suggest-${s.replace(/\./g, '-')}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100">{s}</button>)}
        </div>
      )}
    </Field>
  );
}
